import { Component, OnInit, Input, EventEmitter, Output, Inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';

import { Search } from '@nx-ahs/search';
import { AppMenuItem, ApplicationModel } from '@nx-ahs/shared/models';
import { UtilitiesService, AppMenuService } from '@nx-ahs/shared/services';
import { ConditionDirective, MenuIfDirective, PermissionIdDirective } from '@nx-ahs/shared/directives';

interface SecurityServiceLike {
	getCurrentApp(): ApplicationModel;
}

import { UserMenuPreferencesService } from '../../../services/user-menu-preferences.service';
import { Subscription } from 'rxjs';
import { SearchService } from '../../../services/search.service';
import { BreadcrumbService } from '../../../services/breadcrumb.service';

@Component({
	selector: 'application-menu-menu-items-list',
	templateUrl: './application-menu-menu-items-list.component.html',
	styleUrls: ['./application-menu-menu-items-list.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		FontAwesomeModule,
		MatCardModule,
		MatDividerModule,
		MatMenuModule,
		FlexLayoutModule,
		ConditionDirective,
		PermissionIdDirective
	]
})
export class ApplicationMenuMenuItemsListComponent implements OnInit, OnDestroy {
	public activeItem: string;
	public searches: Search[];
	public menuItems: AppMenuItem[];
	public menuPaddingStyle: string;

	@Input()
	public allowLabelToggle: boolean = true;

	@Input()
	public nestLevel: number;

	@Input()
	public paddingPx: number;

	@Input()
	set menuConfig(data: AppMenuItem[]) {
		this._menuConfig = data;
		this.updateMenu();
	}

	@Input()
	public globalConditionEntities: Record<string, unknown>[]; // not sure what to do with this

	@Output()
	public menuItemClicked: EventEmitter<AppMenuItem> = new EventEmitter<AppMenuItem>();

	@Output()
	public menuUpdatedEvent: EventEmitter<AppMenuItem> = new EventEmitter<null>();

	public noActionsAvailable: boolean;

	private subscriptions: Subscription[] = [];
	private _menuConfig: AppMenuItem[] = [];
	public _mainMenuClicked: boolean = false;
	constructor(
		@Inject('securityService') private securityService: SecurityServiceLike,
		private router: Router,
		private searchService: SearchService,
		private utilities: UtilitiesService,
		private userMenuPreferencesService: UserMenuPreferencesService,
		private appMenuService: AppMenuService,
		private breadcrumbService: BreadcrumbService
	) {}

	ngOnInit() {
		this.menuPaddingStyle = `padding-left: ${(this.nestLevel + 1) * this.paddingPx}px`;

		if (this._menuConfig && this._menuConfig.length > 0) {
			// Run through all menu items to figure out if any will be displayed based on their conditions
			// It is possible on Tasks to have several would-be-available options all get removed conditionally
			let currentUserPermissions: number[];
			const currentApp = this.securityService.getCurrentApp();
			if (currentApp) {
				currentUserPermissions = currentApp.permissions;
			}

			let count: number = 0;
			this._menuConfig.forEach((mi) => {
				if (
					MenuIfDirective.canDisplayItem(
						currentUserPermissions,
						mi,
						this.utilities,
						this.globalConditionEntities
					)
				) {
					count++;
				}
			});

			this.noActionsAvailable = count == 0;
		} else {
			// No way to be sure if this is a totally customized menu like the upper left system menu...
			// But in that case you should be figuring it out yourself anyway, right?
			this.noActionsAvailable = false;
		}

		this.subscriptions.push(
			this.searchService.searches$.subscribe(async (_newSearches) => {
				this.updateMenu();
			})
		);
		this.subscriptions.push(
			this.breadcrumbService.breadcrumbArray.subscribe((crumbs) => {
				//if (!this._mainMenuClicked) {
				crumbs.forEach((x) => {
					const temp = this._menuConfig.find((y) => y.indexId == x.menuItem.indexId); // all breadcrumbs should result in an expanded menu
					if (temp) {
						temp.expanded = true;
						this.appMenuService.calculateExpanded(temp);
					}
				});
				//}
			})
		);
	}

	ngOnDestroy() {
		this.subscriptions.forEach((sub) => {
			sub.unsubscribe();
		});
	}

	public miClicked(mi: AppMenuItem, event?: Event): void {
		this.appMenuService.setActiveMenuItem(mi.indexId);

		// If the menu item has a child menu then toggle the expansion of the child menu
		if (mi.childMenu && mi.childMenu.length > 0) {
			// Toggle expansion if allowed
			if ((!mi.expanded || !mi.disableCollapse) && this.allowLabelToggle) {
				if (event) event.stopPropagation();

				mi.expanded = !mi.expanded;
				this._mainMenuClicked = true;
				if (this.appMenuService.getMenuMode() == 'home') this.appMenuService.calculateExpanded(mi);
			}

			if (mi.alwaysEmit) {
				this.menuItemClicked.emit(mi);
			}
		} else if (mi.url) {
			//Testing this item for ri
			if (mi.url.includes('https://')) window.open(mi.url);
			// If a URL was provided then navigate to it
			else this.router.navigateByUrl(mi.url);

			// Emit event so subscribers can handle it
			this.menuItemClicked.emit(mi);
		} else if (mi.methodName) {
			// Emit event so subscribers can handle it
			this.menuItemClicked.emit(mi);
		}

		// No handler is present for this type of menu item
		else {
			alert('Not implemented');
		}
		// update breadcrumbs last
		this.breadcrumbService.menuToBreadcrumb(mi);
		//this._mainMenuClicked = false;
	}

	public menuUpdated() {
		this.menuUpdatedEvent.emit();
	}

	public async toggleFavorite(mi: AppMenuItem, event?: Event): Promise<void> {
		// Disable the click event from continuing through the chain
		if (event) {
			event.stopPropagation();
		}

		if (mi.favorited == null || mi.favorited == false) {
			mi.favorited = true;
			await this.userMenuPreferencesService.addFavoriteMenuItem(mi.menuItemId);
		} else if (mi.favorited == true) {
			mi.favorited = false;
			await this.userMenuPreferencesService.removeFavoriteMenuItem(mi.menuItemId);
		}
	}

	public getMenuTitle(mi: AppMenuItem) {
		let title = `${mi.title}`;

		if (mi.count) title = title + ` (${mi.count})`;

		return title;
	}

	public getActiveMenuItem(): string {
		if (this.appMenuService.getMenuMode() == 'history') return '0';

		return this.appMenuService.getActiveMenuItem();
	}

	private updateMenu() {
		this._menuConfig.forEach(async (mi) => {
			if (mi.title === 'Search' || mi.title === 'HealthTrack Search') {
				await this.loadSearches(mi);
			}
		});

		this.menuItems = this._menuConfig;
	}

	private async loadSearches(mi: AppMenuItem): Promise<void> {
		const permissionIds: number[] = this.securityService.getCurrentApp().permissions;

		const searchesData = await this.searchService.getSearches();

		if (searchesData && searchesData.length > 0) {
			// Show the searches available in the UI
			let searches = searchesData.filter((x) => x.showInSearchTool == true);

			//filter searches based on user having permission to execute them if permissions are supplied
			if (permissionIds.length > 0) {
				searches = searches?.filter((x) => permissionIds.includes(x.permissionId) || x.permissionId == null);
			}

			// in case there are additional searches aside from those defined in the database (i.e. NC SCUBI), preserve the childMenu to be added after
			let childMenuCopy: AppMenuItem[] = null;

			if (mi.childMenu != null) {
				childMenuCopy = mi.childMenu?.filter((cm) => cm.metadata?.preserveForMenuLoad == true);
			}

			mi.childMenu = [];
			const defaultSearchMenuItemId = mi['menuItemId'];

			if (searches?.length > 0) {
				searches?.forEach((s) => {
					const indexId = `${mi.indexId}.${s.searchId.toString()}`;
					const secondId = indexId.replace(/[.]/g, '');

					const splicedId = defaultSearchMenuItemId.slice(
						0,
						defaultSearchMenuItemId.length - secondId.length
					);

					mi.childMenu.push({
						title: s.name,
						url: 'searches/search-list/' + s.searchId,
						indexId: indexId,
						menuItemId: splicedId + secondId,
						disableCollapse: false
					} as AppMenuItem);
				});

				if (childMenuCopy?.length > 0) {
					childMenuCopy?.forEach((cm) => {
						mi.childMenu.push(cm);
					});
				}

				mi.childMenu = await this.userMenuPreferencesService.markMainMenuConfigFavorites(mi.childMenu);
				this.menuUpdatedEvent.emit();
			}
		}
	}
}
